import { DSButton, DSFlex, DSIcon, DSText } from '@zoe/ds-web'
import React, { useContext, useEffect, useState } from 'react'
import styled from 'styled-components'

import { type CountryDisplayInfo, COUNTRY_DISPLAY_BY_CODE, countryCodeToLocale } from '@/types/locales'

import { GeoContext } from '@/context/geoContext'
import { useLocale } from '@/context/localeContext'
import { TrackingContext } from '@/context/trackingContext'

import { useTrackedRouter } from '@/lib/attribution/useTrackedRouter'
import { trackCtaClick } from '@/lib/gtm/gtmV2'
import { useHasMounted } from '@/lib/hooks/useHasMounted'
import { SESSION_STORAGE_KEYS } from '@/lib/storage-keys'
import { getLocalesForRoutePattern } from '@/lib/utils/localisationUtils'

const StyledLocaleMismatchBanner = styled.div`
  background-color: var(--color-surface-navy-bold);
  z-index: 5;
  padding: var(--grid-8);
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  max-width: 100%;
  display: flex;
  justify-content: center;
`

export const LocaleMismatchBanner = () => {
  const { country } = useContext(GeoContext)
  const { currentLocale } = useLocale()
  const { section } = useContext(TrackingContext)
  const router = useTrackedRouter()
  const hasMounted = useHasMounted()
  const [isBannerVisible, setIsBannerVisible] = useState(true)
  const [currentCountry, setCurrentCountry] = useState<CountryDisplayInfo | undefined>(undefined)

  useEffect(() => {
    if (!hasMounted || !router.isReady || !country) return
    const countryCode = country as keyof typeof COUNTRY_DISPLAY_BY_CODE
    if (countryCode in COUNTRY_DISPLAY_BY_CODE) {
      setCurrentCountry(COUNTRY_DISPLAY_BY_CODE[countryCode])
    }
  }, [hasMounted, router.isReady, country])

  useEffect(() => {
    if (!hasMounted) return
    if (sessionStorage.getItem(SESSION_STORAGE_KEYS.LOCALE_BANNER_DISMISSED) === '1') {
      setIsBannerVisible(false)
    }
  }, [hasMounted])

  const allowedLocalesForPage = getLocalesForRoutePattern(router.pathname)

  if (!hasMounted) return null
  if (!country || !currentCountry) return null
  if (currentLocale === currentCountry.locale) return null
  // Only suggest a locale the current page actually supports — base pages stay GB/US,
  // registry PDPs allow whatever locales their NEXT_PUBLIC_*_LOCALES env list contains.
  if (!allowedLocalesForPage.includes(currentCountry.locale)) return null

  const handleTakeMeThere = () => {
    dismissLocaleMismatchBanner()
    const targetLocale = countryCodeToLocale(country)
    const pathWithoutLocale = router.asPath.replace(/^\/[^/]+/, '')
    window.location.assign(`/${targetLocale}${pathWithoutLocale}`)
    trackCtaClick(section || 'homepage', 'Locale banner - Take me to the local store')
  }

  const handleStayHere = () => {
    dismissLocaleMismatchBanner()
    trackCtaClick(section || 'homepage', 'Locale banner - Stay here')
  }

  const handleDismissBanner = () => {
    dismissLocaleMismatchBanner()
    trackCtaClick(section || 'homepage', 'Locale banner - Dismiss')
  }

  const dismissLocaleMismatchBanner = () => {
    try {
      sessionStorage.setItem(SESSION_STORAGE_KEYS.LOCALE_BANNER_DISMISSED, '1')
    } catch {
      // sessionStorage may be unavailable (e.g. private browsing)
    }
    setIsBannerVisible(false)
  }

  return (
    isBannerVisible && (
      <StyledLocaleMismatchBanner role="banner" aria-live="polite">
        <DSFlex
          breakConfig={{
            maxViewportDirection: 'row',
            breakAt: {
              breakPoint: 'm',
              direction: 'column',
            },
          }}
          justifyContent="space-between"
          alignItems="center"
          gap={16}
          width="1524px"
        >
          <DSText
            variant="fluid-paragraph-200"
            color="primary-inverse"
            align="left"
            breakAt={{ breakPoint: 'm', align: 'center' }}
          >
            Looks like you&apos;re in{' '}
            <DSText as="span" variant="fluid-paragraph-200" weight="semiBold" color="primary-inverse">
              {currentCountry.flag} {currentCountry.name}
            </DSText>
            . Would you like to visit your local store?
          </DSText>
          <DSFlex direction="row" alignItems="center" gap={16}>
            <DSButton
              variant="primary"
              colorScheme="default"
              size="small"
              width="auto"
              onPress={handleTakeMeThere}
              aria-label="Take me to the local store"
            >
              <DSText as="span" variant="fluid-paragraph-200" weight="semiBold" color="primary">
                Take me there
              </DSText>
            </DSButton>
            <DSButton
              variant="secondary"
              colorScheme="inverse"
              size="small"
              width="auto"
              onPress={handleStayHere}
              aria-label="Stay on current site"
            >
              <DSText as="span" variant="fluid-paragraph-200" weight="semiBold" color="primary-inverse">
                Stay here
              </DSText>
            </DSButton>
            <DSButton
              variant="tertiary"
              size="small"
              width="auto"
              onPress={handleDismissBanner}
              aria-label="Dismiss locale banner"
            >
              <DSIcon name="close" size={24} color="inverse" aria-hidden="true" />
            </DSButton>
          </DSFlex>
        </DSFlex>
      </StyledLocaleMismatchBanner>
    )
  )
}
